import { useHistory } from 'react-router-dom';
import * as React from 'react';

import { LodgingOnSubmitCallback, SearchForm, SearchFormInputs, useLodgingURLParamsToQueryInputs } from '@shared-ui/search-tools-lodging';
import { RefTarget } from '@shared-ui/ref-context';

import { HOUSE_RULES_GROUP } from 'src/common/constants/filters';

import { EssClientLogger } from 'typeahead-client-logger';
import { parse } from 'query-string';

interface SearchFormWrappingProps {
  handleSubmit: LodgingOnSubmitCallback;
  essLogger: EssClientLogger;
  showDatePicker: boolean;
  autoOpenPlayback: boolean;
  stickySearchForm: boolean;
  showWeekday: boolean;
  toggleDatePicker: (shouldOpen: boolean) => void;
  dateLocalFormat?: string;
  queryParamStartDate: string;
  queryParamEndDate: string;
  queryParamLatLong?: string;
  alternateDestination: boolean;
  dateFieldInitialFocus?: boolean;
}

const getDate = (date: string, dateLocalFormat: string) => {
  const dateParts = date.split('/');
  if (!dateParts[0] || !dateParts[1] || !dateParts[2]) return null;
  if (dateLocalFormat === 'd/M/yyyy' && /^(0?[1-9]|[12][0-9]|3[01])\/(0?[1-9]|1[0-2])\/\d{4}$/.test(date)) {
    return {
      year: parseInt(dateParts[2], 10),
      month: parseInt(dateParts[1], 10),
      day: parseInt(dateParts[0], 10),
    };
  }

  if (dateLocalFormat === 'M/d/yyyy' && /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/\d{4}$/.test(date)) {
    return {
      year: parseInt(dateParts[2], 10),
      month: parseInt(dateParts[0], 10),
      day: parseInt(dateParts[1], 10),
    };
  }

  return null;
};

const getFinalDateInputs = (
  inputs: SearchFormInputs | null,
  inputStartDate: { year: number; month: number; day: number } | null,
  inputEndDate: { year: number; month: number; day: number } | null
) => {
  if (inputs?.propertySearchCriteriaInput) {
    if (inputStartDate && inputEndDate) {
      return {
        ...inputs,
        propertySearchCriteriaInput: {
          ...inputs?.propertySearchCriteriaInput,
          primary: {
            ...inputs.propertySearchCriteriaInput.primary,
            dateRange: {
              ...inputs?.propertySearchCriteriaInput?.primary.dateRange,
              checkInDate: inputStartDate,
              checkOutDate: inputEndDate,
            },
          },
          secondary: {
            ...inputs.propertySearchCriteriaInput.secondary,
            selections: [],
          },
        },
      };
    }

    return {
      ...inputs,
      propertySearchCriteriaInput: {
        ...inputs?.propertySearchCriteriaInput,
        primary: {
          ...inputs.propertySearchCriteriaInput.primary,
        },
        secondary: {
          ...inputs.propertySearchCriteriaInput.secondary,
          selections: [],
        },
      },
    };
  }

  return inputs;
};

export const SearchFormWrapping: React.FC<SearchFormWrappingProps> = ({
  handleSubmit,
  essLogger,
  showDatePicker,
  toggleDatePicker,
  autoOpenPlayback,
  stickySearchForm,
  showWeekday,
  dateLocalFormat,
  queryParamStartDate,
  queryParamEndDate,
  queryParamLatLong,
  alternateDestination,
  dateFieldInitialFocus,
}) => {
  const inputs = useLodgingURLParamsToQueryInputs();
  const inputStartDate = getDate(queryParamStartDate, dateLocalFormat ?? '');
  const inputEndDate = getDate(queryParamEndDate, dateLocalFormat ?? '');
  const sortFilter = inputs?.propertySearchCriteriaInput?.secondary?.selections;
  const finalInputs = (inputStartDate && inputEndDate) || sortFilter ? getFinalDateInputs(inputs, inputStartDate, inputEndDate) : inputs;

  const history = useHistory();
  const [skipQuery, setSkipQuery] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const isAnyPetAllowed = inputs?.propertySearchCriteriaInput?.secondary?.selections?.some(
    selection => selection.value === HOUSE_RULES_GROUP.PETS_ALLOWED
  );

  React.useEffect(() => {
    /* istanbul ignore next */
    if (history.action === 'POP' && !isDialogOpen) {
      setSkipQuery(false);
    } else if (inputs && !skipQuery) {
      setSkipQuery(true);
    }
    setIsDialogOpen(Boolean(parse(history.location.search).pwaDialog));
  }, [inputs, skipQuery, history.action]);

  const testId = `${stickySearchForm ? 'sticky-' : ''}search-form1${showWeekday ? '-with-showWeekday' : ''}${
    alternateDestination ? '-with-alternateDestination' : ''
  }`;

  return (
    <RefTarget name="SearchForm" dataStid={testId}>
      <SearchForm
        inputs={finalInputs ?? {}}
        onSubmit={handleSubmit}
        includePlayback
        autoOpenPlayback={autoOpenPlayback}
        skipQuery={skipQuery}
        essLogger={essLogger}
        showCalendar={showDatePicker}
        toggleCalendar={toggleDatePicker}
        autoSubmitOnFieldChange
        isAnyPetAllowed={isAnyPetAllowed}
        sticky={stickySearchForm}
        showWeekday={showWeekday}
        additionalAdapterFields={alternateDestination ? { alternate: true, lastSearchLatLong: queryParamLatLong } : {}}
        dateFieldInitialFocus={dateFieldInitialFocus}
      />
    </RefTarget>
  );
};

SearchFormWrapping.displayName = 'SearchFormWrapping';
